import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { STAGES, StageItem } from './sidebar.model';
import { CostMeterComponent } from './cost-meter/cost-meter.component';
import { ProjectSessionService } from '../../../../core/project-session/project-session.service';

/**
 * SidebarComponent — left navigation panel for AZ Symphony workflow stages.
 *
 * Displays all nine workflow stages with Material Symbols Outlined icons and labels.
 * Emits stage selection events to the parent AppComponent.
 *
 * Collapse/expand functionality is deferred to a future story.
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [MatIconModule, CostMeterComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.collapsed]': 'collapsed()',
  },
})
export class SidebarComponent {
  private readonly projectSession = inject(ProjectSessionService);
  private readonly router = inject(Router);

  /** Controls collapsed visual state. Default: false (expanded). */
  collapsed = input<boolean>(false);

  /** 0-based index of the currently active stage. Default: 0. */
  activeIndex = input<number>(0);

  /** 0-based indices of stages that have been completed. Default: empty set (no completed stages). */
  completedIndices = input<ReadonlySet<number>>(new Set<number>());

  /** Whether the stage flow nav renders. False on Home, where there is no current stage to show. */
  showWorkflow = input<boolean>(true);

  /** Emits the 0-based index of the clicked stage item. */
  stageSelected = output<number>();

  readonly stages = STAGES;

  /** True when stage `index` has not yet been unlocked by ProjectSessionService. */
  isLocked(index: number): boolean {
    return !this.projectSession.isUnlocked(index);
  }

  /**
   * True while stage `index`'s background job is still generating. Only
   * meaningful for the two stages backed by an auto-triggered job — UI
   * Feedback (3) and Architecture (4) — and only while still locked, so the
   * indicator disappears once the requester can just look at the result.
   */
  isGeneratingForStage(index: number): boolean {
    if (!this.isLocked(index)) {
      return false;
    }
    if (index === 3) {
      return this.projectSession.uiSpecGenerating();
    }
    if (index === 4) {
      return this.projectSession.blueprintGenerating();
    }
    return false;
  }

  /**
   * Builds the accessible label for a stage button, appending non-visual
   * suffixes for state that is otherwise conveyed only by icon/colour
   * (completed) or by the decorative gold marker (AI-powered).
   */
  ariaLabelFor(stage: StageItem, index: number): string {
    const completedSuffix = this.completedIndices().has(index) ? ' (completed)' : '';
    const aiSuffix = stage.aiPowered ? ', AI-powered stage' : '';
    const generatingSuffix = this.isGeneratingForStage(index) ? ', generating in the background' : '';
    return `${stage.name}${completedSuffix}${aiSuffix}${generatingSuffix}`;
  }

  onStageSelect(index: number): void {
    if (this.isLocked(index)) {
      return; // Clicking a locked stage does nothing.
    }
    this.stageSelected.emit(index);
  }

  goHome(): void {
    this.router.navigate(['/']);
  }
}
